import React from 'react';
import { Form, InputGroup, OverlayTrigger, Popover } from 'react-bootstrap';
import Select, { ValueType } from 'react-select';
import { CardCategory } from '../../models/customer-application';
import { CustomerLocation, isCustomerLocationRequired, LocationOption } from '../../models/customer-location';

interface INewCustomerCardForm {
  cardName: string;
  monthlyLimit?: string;
  cardCategory?: CardCategory;
  isVirtual: boolean;
  customerLocationId?: number;
}

interface PaymentCardSectionProps {
  cards: Record<CardCategory, INewCustomerCardForm[]>;
  setCards: (cards: Record<CardCategory, INewCustomerCardForm[]>) => void;
  saving: boolean;
  categoryLabels: Record<CardCategory, string>;
  categoryIcons: Record<CardCategory, string>;
  customerLocations?: CustomerLocation[];
  validated?: boolean;
}

export const PaymentCardSection: React.FC<PaymentCardSectionProps> = ({
  cards,
  setCards,
  saving,
  categoryLabels,
  categoryIcons,
  customerLocations = [],
  validated = false,
}) => {
  const hasCustomerLocations = isCustomerLocationRequired(customerLocations);
  const locationOptions = customerLocations.map(location => ({
    value: String(location.id),
    label: location.name ?? 'Nimetu osakond'
  }));

  return (
    <>
      {Object.values(CardCategory)
        .filter((cat): cat is CardCategory => cat !== CardCategory.DISCOUNT)
        .map((category: CardCategory) => (
          <div key={category} className="mb-3">
            <div className="mb-2 fw-bold d-flex align-items-center">
              <i className={`icon__${categoryIcons[category]} icon--36 mr-2`}></i>
              {categoryLabels[category]}
              <button
                className="ml-2 btn btn-primary btn-sm"
                type="button"
                onClick={() => {
                  const newCards = { ...cards };
                  newCards[category] = [...newCards[category], { cardName: '', monthlyLimit: undefined, cardCategory: category, isVirtual: false }];
                  setCards(newCards);
                }}
              >
                <i className='icon__plus-white icon--24'></i>
              </button>
            </div>
            {(cards[category] as INewCustomerCardForm[]).map((card, idx: number) => (
              <div key={idx} className="border p-3 mb-2 d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <div className="d-flex flex-row align-items-start mb-2 gap-2">
                    <div className="w-50">
                      <Form.Label>Nimi</Form.Label>
                      <Form.Control
                        required
                        minLength={1}
                        value={card.cardName}
                        onChange={e => {
                          const newCards = { ...cards };
                          newCards[category][idx].cardName = e.target.value;
                          setCards(newCards);
                        }}
                        disabled={saving}
                        type="text"
                        placeholder={`Kaardi nimi ${idx + 1}`}
                      />
                      <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                    </div>
                    <div className="flex-shrink-0" style={{ minWidth: '160px', maxWidth: '200px' }}>
                      <Form.Label>Kuulimiit</Form.Label>
                      <InputGroup>
                        <Form.Control
                          value={card.monthlyLimit ?? ''}
                          pattern="^[0-9]*?[.,]?[0-9]+$"
                          disabled={saving}
                          onChange={e => {
                            const newCards = { ...cards };
                            const value = e.target.value.replace(',', '.');
                            newCards[category][idx].monthlyLimit = value.trim().length > 0 ? value : undefined;
                            setCards(newCards);
                          }}
                          type="text"
                          placeholder="Kuulimiit"
                        />
                        <span className="input-group-text">€</span>
                        <Form.Control.Feedback type="invalid">Limiit peab olema arv</Form.Control.Feedback>
                      </InputGroup>
                    </div>
                    <div className="flex-shrink-0 d-flex flex-column">
                      <Form.Label className="invisible">&nbsp;</Form.Label>
                      <OverlayTrigger
                        trigger={["hover", "focus"]}
                        placement="right"
                        overlay={
                          <Popover id={`monthly-limit-popover-${category}-${idx}`}>
                            <Popover.Content>Kuulimiiti saab igal ajal muuta iseteenidusest</Popover.Content>
                          </Popover>
                        }
                      >
                        <span
                          className="d-flex align-items-center"
                          style={{ cursor: "pointer", height: '38px', color: "#0d6efd" }}
                          tabIndex={0}
                          aria-label="Info"
                        >
                          &#9432;
                        </span>
                      </OverlayTrigger>
                    </div>
                  </div>
                  {hasCustomerLocations && (
                    <div className="w-50 mb-2">
                      <Form.Label>Osakond</Form.Label>
                      <Select
                        inputId={`card-location-${category}-${idx}`}
                        className={'select-terminal ' + (card.customerLocationId == null && validated ? 'invalid' : '')}
                        classNamePrefix="select"
                        options={locationOptions}
                        isDisabled={saving}
                        placeholder="Vali osakond"
                        noOptionsMessage={() => 'Vasted puuduvad'}
                        value={locationOptions.find(option => option.value === String(card.customerLocationId)) ?? null}
                        onChange={(option: ValueType<LocationOption>) => {
                          const selected = option as LocationOption | null;
                          const newCards = { ...cards };
                          newCards[category][idx].customerLocationId = selected ? Number(selected.value) : undefined;
                          setCards(newCards);
                        }}
                      />
                      {card.customerLocationId == null && validated && (
                        <div className="invalid-feedback d-block">Kohustuslik väli</div>
                      )}
                    </div>
                  )}
                  <Form.Check
                    id={`'check-is-virtual-card-${category}-${idx}`}
                    custom
                    type="checkbox"
                    label="Soovin virtuaalset kaarti"
                    onChange={() => {
                        const newCards = { ...cards };
                        newCards[category][idx].isVirtual = !newCards[category][idx].isVirtual;
                        setCards(newCards);
                      }}
                    checked={card.isVirtual}
                  />
                </div>
                {(cards[category] as INewCustomerCardForm[]).length > 0 && (
                  <div
                    className="icon__delete cursor-pointer flex-shrink-0"
                    onClick={() => {
                      const newCards = { ...cards };
                      newCards[category] = newCards[category].filter((_card, i: number) => i !== idx);
                      setCards(newCards);
                    }}
                    title="Eemalda kaart"
                    role="button"
                    tabIndex={0}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
    </>
  );
};
